import { createContext, useContext } from 'react';

import { Color } from 'ui/ColorPicker';

import { CustomUser, Sankey } from '../generated-api';

export const CorporateBrandingContext = createContext<{
  openCorporateBrandingDrawer: () => void;
}>({
  openCorporateBrandingDrawer: () => {},
});

export const useCorporateBrandingContext = () =>
  useContext(CorporateBrandingContext);

export type TeamSettings = {
  brand?: {
    colors?: Color[];
  };
};

export const initialTeamSettings: TeamSettings = {
  brand: {
    // Using our own Design System colors for the initial branding:
    // https://www.figma.com/design/J3BIKTtXAgKfDKmeW2sYjT/Branding-Proposal?node-id=86-830&t=QnAOrkz6Iy8Q3eOh-0
    colors: [
      { label: 'Neutral', value: '#696969', keywords: [], isDefault: true },
      {
        label: 'Red',
        value: '#D1003F',
        keywords: [
          'expense',
          'cost',
          'loss',
          'tax',
          'r&d',
          'g&a',
          's&m',
          'administrative',
          'marketing',
          'development',
          'depreciation',
          'amortization',
          'salaries',
          'rent',
          'restructuring',
        ],
      },
      {
        label: 'Green',
        value: '#00A34C',
        keywords: ['profit', 'income', 'earning'],
      },
      { label: 'Yellow', value: '#B88400', keywords: [] },
      { label: 'Blue', value: '#0077B8', keywords: [] },
    ],
  },
};

export const TeamContext = createContext<{
  hasInitialized: boolean;
  isSaving: boolean;
  teamSettings: TeamSettings;
  saveSettings: (settings: TeamSettings) => Promise<void>;
}>({
  hasInitialized: false,
  isSaving: false,
  teamSettings: initialTeamSettings,
  saveSettings: async () => {},
});

export const useTeamContext = () => useContext(TeamContext);

// NOTE: This is the frontend method corresponding to Django's can_user_edit_diagram
export const canUserEditDiagram = (
  sankey: Sankey | undefined,
  user: CustomUser | undefined
) => {
  if (!user) {
    return false;
  }

  const isMyDiagram = sankey?.owner_id === user.id;
  const isTeamOwnedDiagram = !!user.default_team?.members
    .map((m) => m.user_id)
    .includes(sankey?.owner_id ?? 0);

  return (
    isMyDiagram || (isTeamOwnedDiagram && sankey?.team_permission === 'edit')
  );
};
